/**
 * Application-surface resolution.
 *
 * The same Vite/React bundle serves two distinct product surfaces off different
 * hostnames while sharing one backend runtime:
 *   - `workforce` — the existing Raqib360 / Nexus marketing + Admin app.
 *   - `social`    — the consumer Social / Gatherings experience served under a
 *                   dedicated consumer host (e.g. ha.nchehab.ddns.net).
 *
 * ALL hostname → surface decisions live here so React components never compare
 * `window.location.hostname` directly. To add a production social domain, extend
 * `VITE_SOCIAL_APP_HOSTS` (comma-separated) — no code change required. Anything
 * not listed (including localhost by default) stays on the workforce surface, so
 * existing behaviour is preserved.
 */

export type AppSurface = 'workforce' | 'social'
export type AppEntrySurface = 'login' | AppSurface

/** Normalize a hostname for comparison: lowercase, trimmed, port stripped. */
function normalizeHost(hostname: string | null | undefined): string {
  return String(hostname ?? '')
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, '')
}

/** Parse a comma-separated host list (from VITE_SOCIAL_APP_HOSTS) into hosts. */
export function parseSocialHosts(raw: string | null | undefined): string[] {
  return String(raw ?? '')
    .split(',')
    .map((host) => normalizeHost(host))
    .filter(Boolean)
}

/** Configured social hosts from the build/runtime env (VITE_SOCIAL_APP_HOSTS). */
export function socialAppHosts(): string[] {
  const raw =
    typeof import.meta !== 'undefined' && import.meta.env
      ? (import.meta.env.VITE_SOCIAL_APP_HOSTS as string | undefined)
      : undefined
  return parseSocialHosts(raw)
}

/**
 * Resolve which application surface a hostname belongs to. The configured social
 * hosts may be passed explicitly (used by tests); by default they come from
 * `VITE_SOCIAL_APP_HOSTS`. Unknown hosts → `workforce`.
 */
export function resolveAppSurface(
  hostname: string | null | undefined,
  hosts: string[] = socialAppHosts()
): AppSurface {
  const host = normalizeHost(hostname)
  if (!host) return 'workforce'
  return hosts.includes(host) ? 'social' : 'workforce'
}

/**
 * Resolve top-level bundle ownership for a browser path. The shared login page
 * always wins over hostname-based product routing; `/auth/login` deliberately
 * remains outside this browser-page exception because it is an API endpoint.
 */
export function resolveAppEntrySurface(
  pathname: string | null | undefined,
  hostname: string | null | undefined,
  hosts: string[] = socialAppHosts()
): AppEntrySurface {
  if (String(pathname ?? '').replace(/\/+$/, '') === '/login') return 'login'
  return resolveAppSurface(hostname, hosts)
}

/** Surface for the current browser window (workforce during SSR/non-browser). */
export function currentAppSurface(): AppSurface {
  if (typeof window === 'undefined') return 'workforce'
  return resolveAppSurface(window.location.hostname)
}

export function currentAppEntrySurface(): AppEntrySurface {
  if (typeof window === 'undefined') return 'workforce'
  return resolveAppEntrySurface(window.location.pathname, window.location.hostname)
}
